import { BaseDevice } from '../../../runtime/BaseDevice'
import type { HidChannel } from '../../../channels/hid/HidChannel'
import { DeviceManifest } from '../../../types/manifest'
import { ReportEvents } from '../../../events/ReportEvents'
import { NoticeEvents } from '../../../events/NoticeEvents'
import { VTCH584DPI } from '../../../chips/wch-ch584/feature-impls/mouse/dpi'
import { VTCH584Config } from '../../../chips/wch-ch584/feature-impls/mouse/config'
import { VTCH584Keys } from '../../../chips/wch-ch584/feature-impls/mouse/mouse-keys'
import { mouseUserCustom } from '../../../types/config'
import { VTCH584PerfCfg } from '../../../chips/wch-ch584/feature-impls/mouse/mouse-performance'
import { VTCH584Firmware } from '../../../chips/wch-ch584/feature-impls/mouse/firmware'
import { VTCH584Pairing } from '../../../chips/wch-ch584/feature-impls/mouse/pairing'
import { ReportBackfill } from '../../../cache/ReportBackfill'
import type { ReportBackfillRule } from '../../../cache/types'
import { CH584_MOUSE_REPORT_RULES } from '../../../chips/wch-ch584/protocols/report-rules'
import { toAbsoluteConfigIndex } from '../../../utils/configSlots'

/** VT_CH584 系列鼠标设备连接协议值常量 */
const CONN_WIRED = 0xff as const
const CONN_WIRELESS = 0xa5 as const

/**
 * VT 系列鼠标设备驱动实现。
 * 该设备形态使用 WCH CH584 协议/芯片能力，但目录按"鼠标设备族"归类。
 *
 * 这是一个 "容器类"，负责组装各个功能模块
 */
export class VTCH584MouseDevice extends BaseDevice {
  /** Phase 4: 上报数据回填引擎实例,用于 close/disconnect 时调用 stop() */
  private readonly reportBackfill: ReportBackfill

  constructor(channel: HidChannel, manifest: DeviceManifest) {
    super(channel, manifest)

    // 自动检测连接类型
    this.detectConnectionType()

    // 组装具体的功能实现
    // 将 Feature 实例直接挂载到 features 容器中
    this.features.dpi = new VTCH584DPI(this)
    this.features.config = new VTCH584Config(this)
    this.features.mouseKeys = new VTCH584Keys(this)
    this.features.mousePerfCfg = new VTCH584PerfCfg(this)
    this.features.firmware = new VTCH584Firmware(this)
    this.features.pairing = new VTCH584Pairing(this)

    // Phase 4: 启动上报回填,让高频字段(CPI档位/回报率/扫描率/cfgSel)通过 report 自动进缓存
    this.reportBackfill = new ReportBackfill(this, CH584_MOUSE_REPORT_RULES as ReportBackfillRule[])
    this.reportBackfill.start()
  }

  /**
   * 检测连接类型 (有线/无线)
   * 策略：
   * 1. 检查 Manifest 定义 (假设第二个 PID 为无线)
   * 2. 检查设备名称是否包含 "Receiver"
   */
  private detectConnectionType() {
    const info = this.channel.deviceInfo
    if (!info) return

    const pids = Array.isArray(this.manifest.hardware.productId)
      ? this.manifest.hardware.productId
      : [this.manifest.hardware.productId]

    let isWireless = false

    // 策略 1: 如果 PID 是列表中的第二个，认为是无线
    if (pids.length > 1 && info.productId === pids[1]) {
      isWireless = true
    }
    // 策略 2: 名称包含 "Receiver" 或 "Dongle"
    else if (info.productName && /Receiver|Dongle/i.test(info.productName)) {
      isWireless = true
    }

    if (isWireless) {
      this.connectionType = CONN_WIRELESS
      console.log(
        `[VTCH584MouseDevice] Detected Wireless Mode (PID: 0x${info.productId.toString(16)})`,
      )
    } else {
      this.connectionType = CONN_WIRED
      console.log(
        `[VTCH584MouseDevice] Detected Wired Mode (PID: 0x${info.productId.toString(16)})`,
      )
    }
  }

  protected onConnected(): void {
    console.log(`[VTCH584MouseDevice] Connected: ${this.manifest.model}`)
    // 初始化读取
    this.features.config?.getCurrentConfig().then((config) => {
      this.currentConfig = config
    })
    this.features.dpi?.getCurrentGear().catch((err) => {
      console.error('[VTCH584MouseDevice] Failed to get current DPI gear:', err)
    })
  }

  protected onDisconnected(): void {
    this.reportBackfill.stop()
  }

  async checkConnect(): Promise<boolean> {
    try {
      const isConnected = this.channel?.isConnected
      if (!isConnected) {
        return false
      }

      await this.features.firmware?.getFirmwareVersion()
      return true
    } catch (_error) {
      console.error('[VTCH584MouseDevice] Failed to get firmware version', _error)
      return false
    }
  }

  protected async handleReport(reportId: number, data: DataView): Promise<void> {
    // console.log(
    //   `[VTCH584MouseDevice] Received report ${reportId}, length: ${data.byteLength}`,
    //   data,
    // )
    if (reportId === 0x08) {
      const userData = this.parseUserCustomData(new Uint8Array(data.buffer))
      // console.log(`[VTCH584MouseDevice] parseUserCustomData: `, userData)
      // 如果上报了cfg配置变更，那么其他变更这次上报就不用发出消息提醒了
      let isReportCfg = false
      const configIndex =
        userData.cfgSel == null
          ? this.currentConfig.index
          : toAbsoluteConfigIndex(userData.cfgSel, this.manifest.capabilities, this.configSlotRoles)
      const reportUserData =
        userData.cfgSel == null || configIndex === userData.cfgSel
          ? userData
          : { ...userData, cfgSlot: configIndex }
      this.emit(ReportEvents.MouseUserData, reportUserData)
      const appUserData =
        userData.cfgSel == null || configIndex === userData.cfgSel
          ? userData
          : { ...reportUserData, cfgSel: configIndex }
      // 同步当前配置
      if (this.currentConfig.index !== configIndex) {
        this.emit(ReportEvents.CfgChanged, appUserData)
        const currentConfig = this.configList.find((config) => config.index === configIndex)
        if (currentConfig) {
          this.currentConfig = currentConfig
        } else {
          const config = this.features.config
          if (config) {
            const configs = await config.getAllConfigs()
            const matchedConfig = configs.find((cfg) => cfg.index === configIndex)
            if (matchedConfig) {
              this.currentConfig = matchedConfig
            }
          }
        }
        this.emit(NoticeEvents.CfgChanged, this.currentConfig)
        isReportCfg = true
      }
      // 电量或充电状态有变化时才抛出事件
      const prevData = this.userCustomData as typeof userData | undefined
      if (
        prevData?.batPercent !== userData.batPercent ||
        prevData?.batStatus !== userData.batStatus
      ) {
        this.emit(ReportEvents.Battery, {
          batPercent: userData.batPercent,
          batStatus: userData.batStatus,
        })
      }

      // 设备切换dpi抛出事件
      if (prevData?.cpiLev !== userData?.cpiLev) {
        const dpiInfo = {
          cpiLev: userData.cpiLev,
          xCpiVal: userData.xCpiVal,
          yCpiVal: userData.yCpiVal,
        }
        this.emit(ReportEvents.DpiGearChanged, dpiInfo)
        if (prevData !== undefined && !isReportCfg) {
          this.emit(NoticeEvents.DpiChanged, dpiInfo)
        }
      }

      // 更新用户自定义数据
      this.userCustomData = appUserData
      // DPI 上报时失效当前档位缓存,确保下次读取拿到最新值(tag-mapping §1 场景B)
      if (prevData?.cpiLev !== userData?.cpiLev) {
        this.cache.invalidateByTag(`dpi:current:${this.currentConfig.index}`)
      }
      // 回报率变化：失效缓存 → 发射 report 事件（供 Web UI 更新） → 发射 notice（首次忽略，与 DPI 一致）
      if (prevData?.rpt !== userData.rpt) {
        this.cache.invalidateByTag(`cfg:${this.currentConfig.index}`)
        const config = {
          reportRate: userData.rpt,
          sensorMode: userData.sensorMode,
        }
        this.emit(ReportEvents.ReportRateChanged, config)
        if (prevData !== undefined && !isReportCfg) {
          this.emit(NoticeEvents.ReportRateChanged, config)
        }
      }
      // 按键扫描率变化：失效缓存 → 发射 report 事件 → 发射 notice
      if (prevData?.kRate !== userData.kRate) {
        this.cache.invalidateByTag(`cfg:${this.currentConfig.index}`)
        this.emit(ReportEvents.ScanRateChanged, userData.kRate)
        if (prevData !== undefined && !isReportCfg) {
          this.emit(NoticeEvents.ScanRateChanged, userData.kRate)
        }
      }
      // 模式切换判断
      const conProtocol = userData.conProtocol
      const lineToWireless = conProtocol === 0 && this.connectionType === CONN_WIRED
      const wirelessToLine = conProtocol === 2 && this.connectionType === CONN_WIRELESS
      if (lineToWireless || wirelessToLine) {
        console.log(
          `[VTCH584MouseDevice] Connection type changed: ${lineToWireless ? 'Line -> Wireless' : 'Wireless -> Line'}`,
        )
      }
    }
  }

  private parseUserCustomData(buffer: Uint8Array): mouseUserCustom {
    // 字节0: Mode (bit0~3:连接方式, bit4~7:传感器类型)
    const modeByte = buffer[0]
    const conProtocol = (modeByte & 0x0f) as 0 | 1 | 2
    const sensor = ((modeByte >> 4) & 0x0f) as 0 | 1 | 2 | 3

    // 字节1: CPI档位
    const cpiLev = buffer[1] as 0 | 1 | 2 | 3 | 4 | 5 | 6

    // 字节2~3: X轴CPI值 (uint16, 小端序)
    const xCpiVal = buffer[2] | (buffer[3] << 8)

    // 字节4~5: Y轴CPI值 (uint16, 小端序)
    const yCpiVal = buffer[4] | (buffer[5] << 8)

    // 字节6: 电量状态标志
    const batStatus = buffer[6] as 0 | 1 | 2

    // 字节7: 电池电量百分比
    const batPercent = buffer[7]

    // 构建基础数据对象
    const result: mouseUserCustom = {
      conProtocol,
      sensor,
      cpiLev,
      xCpiVal,
      yCpiVal,
      batStatus,
      batPercent,
    }

    // 可选字段解析：根据buffer长度自动判断是否存在
    // 字节8: 背光模式
    if (buffer.byteLength > 8) {
      result.blMode = buffer[8] as 0 | 1 | 2 | 3 | 4 | 5
    }

    // 字节9: 按键扫描率
    if (buffer.byteLength > 9) {
      result.kRate = buffer[9] as 0 | 1 | 2 | 3 | 4 | 5 | 6
    }

    // 字节10: 回报率
    if (buffer.byteLength > 10) {
      result.rpt = buffer[10] as 0x01 | 0x02 | 0x04 | 0x08 | 0x81 | 0x82 | 0x84
    }

    // 字节11: 当前选择的配置
    if (buffer.byteLength > 11) {
      result.cfgSel = buffer[11] as 0 | 1 | 2 | 3
    }

    // 字节12~13: 鼠标USB PID (uint16, 小端序)
    if (buffer.byteLength > 13) {
      result.usbPid = buffer[12] | (buffer[13] << 8)
    }

    // 字节14: 按键和滚轮类型 (bit0~3:按键, bit4~7:滚轮)
    if (buffer.byteLength > 14) {
      const keyWheelByte = buffer[14]
      result.keyType = (keyWheelByte & 0x0f) as 0 | 1
      result.wheelType = ((keyWheelByte >> 4) & 0x0f) as 0 | 1
    }

    // 字节15: RF发包类型
    if (buffer.byteLength > 15) {
      result.rfType = buffer[15] as 0 | 1 | 2 | 3
    }

    // 字节16: 传感器模式
    if (buffer.byteLength > 16) {
      result.sensorMode = buffer[16] as 0 | 1 | 2 | 3 | 4 | 5
    }

    // 字节17: RF连接状态和RSSI (bit7:连接状态, bit6~0:RSSI值)
    if (buffer.byteLength > 17) {
      const rfConnByte = buffer[17]
      result.rfConnected = ((rfConnByte >> 7) & 0x01) as 0 | 1
      result.rfRssi = rfConnByte & 0x7f
    }

    return result
  }
}
